/**
 * Chunk-load-error → one-time reload safety net (#1060).
 *
 * During a multi-pod rollout an old-build tab can request a hashed Vite
 * chunk (`/assets/index-<hash>.js`) that the pod it lands on doesn't have.
 * PR #1299 made the server return a clean, body-less, no-store 404 for that
 * miss instead of a JSON body that breaks `import()`. The dynamic import
 * still rejects, though, so without a client handler the React lazy boundary
 * blanks the page until a manual hard refresh.
 *
 * This module converts that rare miss into a single automatic
 * `window.location.reload()`: the reload re-fetches `index.html` (served
 * no-cache) and the now-consistent manifest, and the import succeeds.
 *
 * The hard rule: a reload loop is worse than a white screen. A genuinely
 * broken deploy (every pod missing the chunk) must NOT infinite-loop. So we
 * gate reloads behind a sessionStorage marker with a short TTL — at most one
 * automatic reload per `RELOAD_WINDOW_MS`. If we reloaded recently and the
 * chunk still fails, we stop and let the error surface (the version-drift
 * toast remains the soft prompt; this is only the hard-failure net).
 *
 * This complements — does not duplicate — the `apex:version-drift` toast in
 * App.tsx. The toast is the soft "refresh when convenient" prompt for a
 * detected drift; this fires only on an actual chunk-load failure.
 */

const RELOAD_MARKER_KEY = 'apex:chunk-reload-at';

/** How recently a reload must have happened to count as "already tried". */
export const RELOAD_WINDOW_MS = 10_000;

/**
 * Decide whether a chunk-load failure should trigger an automatic reload.
 *
 * Pure + injectable so the loop-guard is unit-testable without touching the
 * real `sessionStorage` / `Date.now` / `location`.
 *
 * Returns true at most once per `RELOAD_WINDOW_MS`: the first failure records
 * `now` and returns true; a second failure inside the window returns false
 * (we already reloaded — reloading again would loop). Once the window
 * elapses the marker is stale and a fresh failure is allowed to reload again.
 */
export function shouldReloadForChunkError(
  store: Pick<Storage, 'getItem' | 'setItem'>,
  now: number,
): boolean {
  const raw = store.getItem(RELOAD_MARKER_KEY);
  const last = raw === null ? NaN : Number(raw);

  // Recent, valid marker → we already reloaded once for this rollout window.
  // Stop to avoid an infinite reload loop on a genuinely broken deploy.
  if (Number.isFinite(last) && now - last < RELOAD_WINDOW_MS) {
    return false;
  }

  // No marker, unparseable marker, or stale marker → record this attempt and
  // allow the reload.
  store.setItem(RELOAD_MARKER_KEY, String(now));
  return true;
}

/**
 * Heuristic: is this error a dynamic-import / chunk-load failure?
 *
 * Covers the messages browsers and Vite produce when a hashed module fails
 * to load or parse: Chrome/Vite "Failed to fetch dynamically imported
 * module", Firefox "error loading dynamically imported module", webpack-style
 * "Loading chunk N failed" / "ChunkLoadError", and the import/parse
 * "Unexpected token '<'" that results when a miss is served HTML.
 */
export function isChunkLoadError(reason: unknown): boolean {
  if (reason == null) return false;
  const name = typeof reason === 'object' && 'name' in reason ? String((reason as Error).name) : '';
  const message =
    typeof reason === 'string'
      ? reason
      : typeof reason === 'object' && 'message' in reason
        ? String((reason as Error).message)
        : '';
  const haystack = `${name} ${message}`.toLowerCase();
  return (
    haystack.includes('chunkloaderror') ||
    haystack.includes('loading chunk') ||
    haystack.includes('dynamically imported module') ||
    haystack.includes('failed to fetch dynamically') ||
    haystack.includes('error loading dynamically') ||
    haystack.includes("unexpected token '<'") ||
    haystack.includes('importing a module script failed')
  );
}

/**
 * Perform the guarded one-time reload for a chunk-load failure. Returns true
 * if a reload was triggered (caller should stop further handling), false if
 * the loop-guard suppressed it.
 */
export function reloadOnceForChunkError(): boolean {
  let store: Pick<Storage, 'getItem' | 'setItem'>;
  try {
    store = window.sessionStorage;
  } catch {
    // sessionStorage can throw in locked-down/private contexts. Without a
    // place to record the marker we can't guarantee loop-safety, so do NOT
    // reload — a white screen the user can manually refresh beats a loop.
    return false;
  }

  if (!shouldReloadForChunkError(store, Date.now())) {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Install global listeners that catch chunk-load failures escaping the React
 * tree (Vite's `vite:preloadError` event, plus unhandled promise rejections
 * and window errors from a rejected dynamic `import()`).
 *
 * The React <ErrorBoundary> around the lazy routes is the primary catch for
 * render-time failures; these window-level listeners are the backstop for the
 * preload event and any rejection that doesn't surface through a boundary.
 *
 * Returns a disposer for symmetry / tests.
 */
export function installChunkErrorReload(): () => void {
  // Vite fires this when a module preload (`<link rel="modulepreload">` or a
  // lazy import) fails. `event.payload` is the underlying error.
  const onPreloadError = (event: Event) => {
    event.preventDefault();
    reloadOnceForChunkError();
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    if (isChunkLoadError(event.reason)) {
      reloadOnceForChunkError();
    }
  };

  const onError = (event: ErrorEvent) => {
    if (isChunkLoadError(event.error ?? event.message)) {
      reloadOnceForChunkError();
    }
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  window.addEventListener('unhandledrejection', onRejection);
  window.addEventListener('error', onError);

  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
    window.removeEventListener('unhandledrejection', onRejection);
    window.removeEventListener('error', onError);
  };
}
